import {
  ButtonVariants,
  FormControlSizes,
  OrderFormSides,
  orderFormSideToSideEnum,
  type SideEnum,
  ToggleGroup,
} from '@talos/kyoko';

export type HeaderSideSelectorProps = {
  side: string | undefined;
  disabled?: boolean;
  size?: FormControlSizes;
  onChange: (side: SideEnum) => void;
  /** Enable/disable the buy button */
  enableBuy?: boolean;
  /** Enable/disable the sell button */
  enableSell?: boolean;
  /** Enable/disable the two-way button */
  enableTwoWay?: boolean;
} & (
  | {
      showTwoWay?: false | undefined;
      onChange: (side: SideEnum) => void;
    }
  | {
      showTwoWay: true;
      onChange: (side: OrderFormSides) => void;
    }
);

export const HeaderSideSelector = ({
  enableBuy = true,
  enableTwoWay = true,
  enableSell = true,
  showTwoWay,
  side,
  onChange,
  disabled,
  size = FormControlSizes.Default,
}: HeaderSideSelectorProps) => {
  const options: Array<{
    value: OrderFormSides;
    displayName: string;
    buttonVariant: ButtonVariants;
    disabled: boolean;
    testId: string;
  }> = [
    {
      value: OrderFormSides.Buy,
      displayName: 'Buy',
      buttonVariant: ButtonVariants.Positive,
      disabled: !enableBuy,
      testId: 'buy-button',
    },
    ...(showTwoWay
      ? [
          {
            value: OrderFormSides.Twoway,
            displayName: '2 way',
            buttonVariant: ButtonVariants.Primary,
            disabled: !enableTwoWay,
            testId: 'two-way-button',
          },
        ]
      : []),
    {
      value: OrderFormSides.Sell,
      displayName: 'Sell',
      buttonVariant: ButtonVariants.Negative,
      disabled: !enableSell,
      testId: 'sell-button',
    },
  ];

  const selectedValue = side == null && showTwoWay ? OrderFormSides.Twoway : (side as OrderFormSides | undefined);

  const handleSideChange = (nextSide: OrderFormSides) => {
    if (showTwoWay) {
      onChange(nextSide);
      return;
    }
    // options excludes Twoway when !showTwoWay, so nextSide can only be Buy or Sell here.
    onChange(orderFormSideToSideEnum(nextSide as Extract<OrderFormSides, 'Buy' | 'Sell'>));
  };

  return (
    <ToggleGroup<OrderFormSides>
      options={options}
      value={selectedValue}
      onChange={handleSideChange}
      disabled={disabled}
      size={size}
      data-testid="order-form-side"
    />
  );
};
